import { useTranslation } from "react-i18next";
import { PencilRuler, UserRound, Check, AlertTriangle, type LucideIcon } from "lucide-react";
import type { ManualState } from "@arc/contracts";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * The manual lifecycle badge (ADR-010 state), mirroring the legacy status workflow — each state carries a glyph AND a
 * colour (colour is never the sole signal, so it survives the brand-red/destructive clash + colour-blindness):
 * - **draft** → grey · a pencil-and-ruler (being authored)
 * - **ready_for_review** → amber (`attention`) · a person (awaiting a reviewer)
 * - **confirmed** → GREEN · a check (published). Uses the `--success` design token (green-700 in light, green-400 in
 *   dark) as a tint, rather than borrowing the brand `primary` or a hardcoded Tailwind green.
 * - **marked** → red (`destructive`) · a warning triangle (flagged / needs revision). The comment (why) is a hover
 *   tooltip on the badge — `excludeFromTabOrder` (supplementary, not a keyboard stop).
 */
/** Per-state label KEY · tint className · glyph. Exported so the "More filters" status toggles reuse the same
 *  treatment. The label is a key rather than a string because this map is module-level — it cannot call a hook,
 *  and a label baked here would be English for every consumer (ADR-048 Phase 4). */
export const MANUAL_STATE_META: Record<ManualState, { labelKey: `facilityManagement.manualState.${ManualState}`; className: string; Icon: LucideIcon }> = {
  draft: { labelKey: "facilityManagement.manualState.draft", className: "bg-muted text-muted-foreground", Icon: PencilRuler },
  ready_for_review: { labelKey: "facilityManagement.manualState.ready_for_review", className: "bg-attention/10 text-attention", Icon: UserRound },
  confirmed: { labelKey: "facilityManagement.manualState.confirmed", className: "bg-success/10 text-success dark:bg-success/15", Icon: Check },
  marked: { labelKey: "facilityManagement.manualState.marked", className: "bg-destructive/10 text-destructive", Icon: AlertTriangle },
};

export function ManualStateBadge({
  state,
  comment,
  className,
}: {
  state: ManualState;
  comment?: string | null;
  className?: string;
}) {
  const { t } = useTranslation();
  const meta = MANUAL_STATE_META[state];
  const Icon = meta.Icon;
  const badge = (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[0.7rem] font-medium",
        meta.className,
        className,
      )}
    >
      <Icon aria-hidden="true" className="size-3" />
      {t(meta.labelKey)}
    </span>
  );

  // A flagged manual's comment explains WHY — a hover tooltip on the badge (decorative → out of the tab order). Parsed
  // via {@link parseMarkComment} so a migrated manual's legacy JSON reads as text (+ where), not raw `{"section":…}`.
  if (state === "marked" && comment) {
    const { text, where } = parseMarkComment(comment);
    return (
      <TooltipTrigger delay={300} excludeFromTabOrder>
        {badge}
        <Tooltip>
          <span className="block">{text}</span>
          {where ? <span className="text-muted-foreground mt-0.5 block text-xs">{t("manualManagement.badge.reportedIn")} {where}</span> : null}
        </Tooltip>
      </TooltipTrigger>
    );
  }
  return badge;
}

/**
 * The mark reason. Legacy stored it as JSON `{ text, section, step }` (the reported problem + where — migrated manuals
 * carry this shape); the rewrite's own marks are plain free text. Returns the `text` + a "section / step" location
 * (dropping the generic "all"/"alle" sentinel), else the raw string. Mirrors the legacy report view
 * (`ARLMS_MarkManual.js:184-195`) so a migrated manual shows a readable reason instead of raw JSON. Shared by the badge
 * tooltip (the card) + the drawer's release-status block.
 */
export function parseMarkComment(raw: string): { text: string; where: string | null } {
  try {
    const j: unknown = JSON.parse(raw);
    if (j && typeof j === "object" && typeof (j as { text?: unknown }).text === "string") {
      const o = j as { text: string; section?: unknown; step?: unknown; canViewStep?: unknown };
      const name = (v: unknown): v is string => typeof v === "string" && v.trim() !== "";
      // `canViewStep: true` means the server RESOLVED both halves to real rows (ADR-041), so trust them even when a
      // section is genuinely named "Alle" — free text allows it, and matching on the name alone would erase the very
      // location the reporter picked (diff panel). Only when that flag is absent or false do we fall back to reading
      // the placeholder out of the names, which is all a legacy row lets us do.
      const parts =
        o.canViewStep === true
          ? [o.section, o.step].filter(name)
          : [o.section, o.step].filter((v): v is string => name(v) && !/^(all|alle)$/i.test(v.trim()));
      return { text: o.text, where: parts.length > 0 ? parts.join(" / ") : null };
    }
  } catch {
    // not JSON → plain free text (a pre-ADR-041 rewrite mark)
  }
  return { text: raw, where: null };
}
